import React, { useEffect, useState, useMemo } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import styled from "styled-components";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

// === Styled Components ===
const HeatmapWrapper = styled.div`
  background-color: #f9f9f9;
  padding: 20px;
  width: 100%;
`;

const ChartTitle = styled.div`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-weight: bold;
  color: #666;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const InfoIconWrapper = styled.div`
  cursor: pointer;
  color: #666;
  transition: color 0.3s;
  &:hover {
    color: #000;
  }
  svg {
    width: 18px;
    height: 18px;
  }
`;

const FilterRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

const FilterLabel = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #444;
`;

const Select = styled.select`
  padding: 8px 12px;
  font-size: 15px;
  border: 1px solid #ccc;
  border-radius: 4px;
`;

const ChartContainer = styled.div`
  width: 100%;
  height: 500px;
`;

const formatNumber = (value) =>
  value?.toLocaleString("pl-PL").replace(/,/g, " ");

const ZakupyByPolishRegionsHeatmap = ({ zakupyData, clientsData }) => {
  const [regionData, setRegionData] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState("00");

  const availableYears = useMemo(() => {
    const years = new Set();
    zakupyData.forEach((row) => {
      const year = parseInt(row.Rok);
      if (!isNaN(year)) years.add(year);
    });
    return Array.from(years).sort((a, b) => b - a);
  }, [zakupyData]);

  const computeRevenue = (row) =>
    parseFloat(row.K_42) + parseFloat(row.K_43) || 0;

  useEffect(() => {
    if (!zakupyData.length || !clientsData.length || !selectedYear) return;

    const filtered = zakupyData.filter((row) => {
      const year = row.Rok?.toString();
      const month = row.Miesiac?.toString().padStart(2, "0");
      return (
        year === selectedYear.toString() &&
        (selectedMonth === "00" || month === selectedMonth)
      );
    });

    const uniqueSuppliers = [...new Set(filtered.map((r) => r.NrDostawcy?.trim()))];
    const supplierToRegion = {};
    uniqueSuppliers.forEach((nip) => {
      const client = clientsData.find((c) => c.nip?.trim() === nip);
      supplierToRegion[nip] = client?.wojewodztwo || "Nieznany";
    });

    const grouped = filtered.reduce((acc, row) => {
      const revenue = computeRevenue(row);
      const region = supplierToRegion[row.NrDostawcy?.trim()] || "Nieznany";
      if (!acc[region]) acc[region] = { region, totalRevenue: 0, transactions: 0 };
      acc[region].totalRevenue += revenue;
      acc[region].transactions += 1;
      return acc;
    }, {});

    const formatted = Object.values(grouped)
      .filter((item) => item.region !== "Nieznany" && item.region !== "Brak danych")
      .map((item) => ({
        ...item,
        avgValue: item.transactions > 0 ? item.totalRevenue / item.transactions : 0,
      }))
      .sort((a, b) => b.totalRevenue - a.totalRevenue);

    setRegionData(formatted);
  }, [zakupyData, clientsData, selectedYear, selectedMonth]);

  useEffect(() => {
    if (availableYears.length > 0 && selectedYear === null) {
      setSelectedYear(availableYears[0]);
    }
  }, [availableYears, selectedYear]);

  if (!regionData.length) return <p style={{ textAlign: "center" }}>Ładowanie danych...</p>;

  const heatmapData = {
    labels: regionData.map((item) => item.region),
    datasets: [
      {
        label: "Całkowite Zakupy",
        data: regionData.map((item) => item.totalRevenue),
        backgroundColor: "#9848c7",
        yAxisID: "y",
        type: "bar",
      },
      {
        label: "Liczba Transakcji",
        data: regionData.map((item) => item.transactions),
        backgroundColor: "#4caf50",
        yAxisID: "y1",
        type: "bar",
      },
      {
        label: "Średnia Wartość Transakcji",
        data: regionData.map((item) => item.avgValue),
        borderColor: "#ff9800",
        backgroundColor: "rgba(255, 152, 0, 0.2)",
        yAxisID: "y2",
        type: "line",
        tension: 0.3,
        fill: false,
        pointRadius: 3,
        pointHoverRadius: 5,
      },
    ],
  };

  const heatmapOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "bottom" },
      tooltip: {
        backgroundColor: "#333",
        titleColor: "#fff",
        bodyColor: "#fff",
        callbacks: {
          label: (context) =>
            `${context.dataset.label}: ${formatNumber(context.raw)}`
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Województwa" },
        ticks: { autoSkip: false, maxRotation: 45, minRotation: 45 },
      },
      y: {
        type: "linear",
        position: "left",
        title: { display: true, text: "Całkowite Zakupy" },
        ticks: {
          callback: (value) => formatNumber(value),
        },
      },
      y1: {
        type: "linear",
        position: "right",
        title: { display: true, text: "Liczba Transakcji" },
        grid: { drawOnChartArea: false },
        ticks: {
          callback: (value) => formatNumber(value),
        },
      },
      y2: {
        type: "linear",
        position: "right",
        offset: true,
        title: { display: true, text: "Średnia Wartość" },
        grid: { drawOnChartArea: false },
        ticks: {
          callback: (value) => formatNumber(value),
        },
      },
    },
  };

  return (
    <HeatmapWrapper>
      <ChartTitle>
        Porównanie województw 
        <InfoIconWrapper data-tooltip-id="zakupy-heatmap-tooltip">
          <Info />
        </InfoIconWrapper>
      </ChartTitle>
      <InfoTooltip
        id="zakupy-heatmap-tooltip"
        place="right"
        style={{
          fontSize: "12px",
          fontFamily: "Arial, sans-serif",
          color: "#fff",
        }}
        content={
          <>
            <strong>Zakupy wg województw</strong>
            <br />
            Wykres przedstawia całkowite zakupy,
            <br />
            liczbę transakcji oraz średnią wartość transakcji.
          </>
        }
      />

      <FilterRow>
        <FilterLabel>Rok:</FilterLabel>
        <Select value={selectedYear || ""} onChange={(e) => setSelectedYear(e.target.value)}>
          {availableYears.map((year) => (
            <option key={year} value={year}>{year}</option>
          ))}
        </Select>

        <FilterLabel>Miesiąc:</FilterLabel>
        <Select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}>
          <option value="00">Cały rok</option>
          {[...Array(12).keys()].map((m) => {
            const val = String(m + 1).padStart(2, "0");
            const name = new Date(2000, m).toLocaleString("pl-PL", { month: "long" });
            return (
              <option key={val} value={val}>{name}</option>
            );
          })}
        </Select>
      </FilterRow>

      <ChartContainer>
        <Bar data={heatmapData} options={heatmapOptions} />
      </ChartContainer>
    </HeatmapWrapper>
  );
};

export default ZakupyByPolishRegionsHeatmap;
